import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "./ui/button";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorCount: 0, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    
    this.setState(prev => ({ 
      errorCount: prev.errorCount + 1,
      errorInfo: errorInfo
    }));
    
    // List of recoverable errors (caused by browser extensions)
    const recoverableErrors = [
      'removeChild',
      'insertBefore',
      'appendChild',
      'replaceChild',
      'NotFoundError',
      'Node',
      'not a child'
    ];
    
    const isRecoverable = recoverableErrors.some(msg => 
      error?.message?.includes(msg) || error?.toString()?.includes(msg)
    );
    
    // If it's a recoverable error and we haven't tried too many times, auto-recover
    // But DON'T auto-recover more than once to prevent loops
    if (isRecoverable && this.state.errorCount < 1) {
      setTimeout(() => {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, 150);
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, errorCount: 0, errorInfo: null });
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorCount: 0, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError && this.state.errorCount >= 3) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-slate-50">
          <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">
              Algo salió mal
            </h2>
            <p className="text-sm text-slate-600 mb-6">
              Esto puede ser causado por extensiones del navegador o una conexión inestable.
              No te preocupes, tus datos están seguros.
            </p>
            <div className="space-y-3">
              <Button onClick={this.handleRetry} variant="outline" className="w-full">
                Reintentar
              </Button>
              <Button onClick={this.handleReload} className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Recargar página
              </Button>
              <Button onClick={this.handleGoHome} variant="ghost" className="w-full text-slate-500">
                <Home className="h-4 w-4 mr-2" />
                Ir al inicio
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Tip: Prueba en modo incógnito (Ctrl+Shift+N) si el problema persiste
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
